import { useState, useEffect, useContext } from 'react';
import { AppContext } from '@/App';
import axios from 'axios';
import { AlertTriangle, Plus, Search, Clock, CheckCircle, XCircle, Settings } from 'lucide-react';

function ReportShutdown() {
  const { API } = useContext(AppContext);
  const [shutdowns, setShutdowns] = useState([]);
  const [machinery, setMachinery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReportModal, setShowReportModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');

  const [newReport, setNewReport] = useState({
    machineId: '',
    machineName: '',
    reportedBy: '',
    shift: 'Day',
    reason: '',
    priority: 'High'
  });

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const fetchData = async () => {
    try {
      const [shutdownRes, machineryRes] = await Promise.all([
        axios.get(`${API}/maintenance/shutdowns`, { params: { status: statusFilter },  }),
        axios.get(`${API}/maintenance/machinery`)
      ]);
      setShutdowns(shutdownRes.data);
      setMachinery(machineryRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMachineSelect = (machineId) => {
    const machine = machinery.find(m => m.id === machineId);
    setNewReport({
      ...newReport,
      machineId,
      machineName: machine?.name || ''
    });
  };

  const handleSubmitReport = async () => {
    try {
      await axios.post(`${API}/maintenance/shutdowns`, newReport);
      setShowReportModal(false);
      setNewReport({ machineId: '', machineName: '', reportedBy: '', shift: 'Day', reason: '', priority: 'High' });
      fetchData();
    } catch (error) {
      console.error('Error reporting shutdown:', error);
      alert('Failed to report shutdown');
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Critical': return 'bg-red-100 text-red-700 border-red-200';
      case 'High': return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'Medium': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'Low': return 'bg-green-100 text-green-700 border-green-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Open': return 'bg-red-100 text-red-700';
      case 'Resolved': return 'bg-green-100 text-green-700';
      case 'Awaiting Parts': return 'bg-yellow-100 text-yellow-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getShiftColor = (shift) => {
    switch (shift) {
      case 'Day': return 'bg-amber-100 text-amber-700';
      case 'Night': return 'bg-indigo-100 text-indigo-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const stats = {
    total: shutdowns.length,
    open: shutdowns.filter(s => s.status === 'Open').length,
    resolved: shutdowns.filter(s => s.status === 'Resolved').length,
    awaitingParts: shutdowns.filter(s => s.status === 'Awaiting Parts').length,
  };

  if (loading) {
    return <div className="text-center py-12">Loading shutdown reports...</div>;
  }

  return (
    <div className="space-y-6" data-testid="report-shutdown-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Report a Shutdown</h1>
          <p className="text-gray-500 mt-1">Log and track machine breakdowns and shutdowns</p>
        </div>
        <button 
          onClick={() => setShowReportModal(true)}
          className="bg-gradient-to-r from-red-600 to-red-700 text-white px-4 py-2.5 rounded-xl text-sm font-medium flex items-center space-x-2 shadow-md"
        >
          <AlertTriangle size={18} />
          <span>Report Shutdown</span>
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
          <p className="text-sm text-gray-500">Total Reports</p>
          <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
        </div>
        <div className="bg-gradient-to-br from-red-50 to-rose-50 rounded-xl p-5 border border-red-200">
          <div className="flex items-center space-x-2">
            <XCircle className="text-red-500" size={18} />
            <p className="text-sm text-red-600 font-medium">Open</p>
          </div>
          <p className="text-2xl font-bold text-red-700">{stats.open}</p>
        </div>
        <div className="bg-gradient-to-br from-yellow-50 to-amber-50 rounded-xl p-5 border border-yellow-200">
          <div className="flex items-center space-x-2">
            <Clock className="text-yellow-600" size={18} />
            <p className="text-sm text-yellow-700 font-medium">Awaiting Parts</p>
          </div>
          <p className="text-2xl font-bold text-yellow-700">{stats.awaitingParts}</p>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-5 border border-green-200">
          <div className="flex items-center space-x-2">
            <CheckCircle className="text-green-500" size={18} />
            <p className="text-sm text-green-600 font-medium">Resolved</p>
          </div>
          <p className="text-2xl font-bold text-green-700">{stats.resolved}</p>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex items-center space-x-4">
        <div className="flex items-center space-x-2 bg-white rounded-xl p-1.5 border border-gray-200">
          {['all', 'Open', 'Awaiting Parts', 'Resolved'].map((status) => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                statusFilter === status
                  ? 'bg-gradient-to-r from-[#1a237e] to-[#283593] text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {status === 'all' ? 'All' : status}
            </button>
          ))}
        </div>
      </div>

      {/* Shutdowns Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-gray-50 to-slate-50">
              <tr>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Report ID</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Machine</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Reported By</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Date/Time</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Shift</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Reason</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Priority</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Downtime</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {shutdowns.map((shutdown) => (
                <tr key={shutdown.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-4 font-mono text-sm text-red-600 font-medium">{shutdown.id}</td>
                  <td className="px-4 py-4">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{shutdown.machineName}</p>
                      <p className="text-xs text-gray-500 font-mono">{shutdown.machineId}</p>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-600">{shutdown.reportedBy}</td>
                  <td className="px-4 py-4 text-sm text-gray-600">
                    {shutdown.reportedDate} <span className="text-gray-400">{shutdown.reportedTime}</span>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getShiftColor(shutdown.shift)}`}>
                      {shutdown.shift}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-600 max-w-xs truncate">{shutdown.reason}</td>
                  <td className="px-4 py-4">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${getPriorityColor(shutdown.priority)}`}>
                      {shutdown.priority}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-sm font-semibold text-gray-900">
                    {shutdown.downtime ? `${shutdown.downtime} hrs` : '-'}
                  </td>
                  <td className="px-4 py-4">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(shutdown.status)}`}>
                      {shutdown.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Report Shutdown Modal */}
      {showReportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900 flex items-center space-x-2">
                <AlertTriangle className="text-red-500" size={24} />
                <span>Report Machine Shutdown</span>
              </h2>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Select Machine</label>
                <select
                  value={newReport.machineId}
                  onChange={(e) => handleMachineSelect(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1a237e]/20 focus:border-[#1a237e] outline-none"
                >
                  <option value="">-- Select Machine --</option>
                  {machinery.filter(m => m.status !== 'Shutdown').map((m) => (
                    <option key={m.id} value={m.id}>{m.name} ({m.id})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reported By</label>
                <input
                  type="text"
                  value={newReport.reportedBy}
                  onChange={(e) => setNewReport({...newReport, reportedBy: e.target.value})}
                  placeholder="Name and designation"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1a237e]/20 focus:border-[#1a237e] outline-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Shift</label>
                  <select
                    value={newReport.shift}
                    onChange={(e) => setNewReport({...newReport, shift: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1a237e]/20 focus:border-[#1a237e] outline-none"
                  >
                    <option value="Day">Day (6 AM - 6 PM)</option>
                    <option value="Night">Night (6 PM - 6 AM)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <select
                    value={newReport.priority}
                    onChange={(e) => setNewReport({...newReport, priority: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1a237e]/20 focus:border-[#1a237e] outline-none"
                  >
                    <option value="Critical">Critical</option>
                    <option value="High">High</option>
                    <option value="Medium">Medium</option>
                    <option value="Low">Low</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason for Shutdown</label>
                <textarea
                  value={newReport.reason}
                  onChange={(e) => setNewReport({...newReport, reason: e.target.value})}
                  placeholder="Describe the issue..."
                  rows={3}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1a237e]/20 focus:border-[#1a237e] outline-none"
                />
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex items-center justify-end space-x-3">
              <button
                onClick={() => setShowReportModal(false)}
                className="px-4 py-2.5 text-gray-600 hover:bg-gray-100 rounded-xl font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitReport}
                disabled={!newReport.machineId || !newReport.reportedBy || !newReport.reason}
                className="px-4 py-2.5 bg-red-600 text-white rounded-xl font-medium disabled:opacity-50"
              >
                Submit Report
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ReportShutdown;
